import type { AccessifyConfig } from '../types';

const CHECKMARK_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>'
);

/** Relative luminance (WCAG) of a #hex colour. Returns NaN for invalid input. */
function relativeLuminance(hex: string): number {
  const m = hex.replace('#', '').match(/^([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (!m) return NaN;
  const full = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const toLin = (v: number) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}

/** Wählt automatisch eine Text-Farbe (schwarz oder weiß) die auf der
 *  übergebenen Hintergrundfarbe ≥ 4.5:1 Contrast erreicht — WCAG AA. */
function pickReadableOnAccent(hex: string): string {
  const lum = relativeLuminance(hex);
  if (!Number.isFinite(lum)) return '#1a0604';
  return lum > 0.18 ? '#1a0604' : '#ffffff';
}

/** Pick between the brand-gradient wordmark (indigo → coral, default)
 *  and a white wordmark variant depending on how dark the panel surface
 *  is. The gradient starts at #3d4775 which sits at lum ≈ 0.063 — on any
 *  brand-surface darker than ~0.20 luminance the left half of the
 *  wordmark blends into the background. The white variant is rendered
 *  unconditionally readable on dark surfaces. */
function pickLogoVariant(surfaceHex: string | undefined): 'gradient' | 'white' {
  if (!surfaceHex) return 'gradient';
  const lum = relativeLuminance(surfaceHex);
  if (!Number.isFinite(lum)) return 'gradient';
  return lum < 0.22 ? 'white' : 'gradient';
}

export function createWidgetStyles(config: AccessifyConfig): string {
  const pos = config.position || 'bottom-right';
  const isRight = pos.includes('right');
  const isBottom = pos.includes('bottom');
  const z = config.zIndex || 2147483647;
  const panelRadius = isRight ? '20px 0 0 20px' : '0 20px 20px 0';
  const hiddenTransform = isRight ? 'translateX(24px)' : 'translateX(-24px)';

  const c = config as any;
  const brandOverrides = [
    c.brandPrimaryColor ? `--surface: ${c.brandPrimaryColor};` : '',
    c.brandSurfaceColor ? `--surface-dim: ${c.brandSurfaceColor};` : '',
    c.brandCardColor ? `--surface-card: ${c.brandCardColor};` : '',
    c.brandHoverColor ? `--surface-hover: ${c.brandHoverColor};` : '',
    c.brandTextColor ? `--text: ${c.brandTextColor};` : '',
    c.brandTextDimColor ? `--text-dim: ${c.brandTextDimColor};` : '',
    c.brandAccentColor ? `--accent: ${c.brandAccentColor}; --accent-soft: ${c.brandAccentColor}26;${c.brandAccentTextColor ? ` --accent-on: ${c.brandAccentTextColor};` : ` --accent-on: ${pickReadableOnAccent(c.brandAccentColor)};`}` : '',
    c.brandBorderColor ? `--border: ${c.brandBorderColor}; --border-hl: ${c.brandBorderColor};` : '',
  ].filter(Boolean).join('\n      ');

  const triggerBg = c.brandTriggerColor || '';
  const triggerIcon = c.brandTriggerIconColor || '';
  // Feste Basis-Breite (= der „320 px"-Standard). Die EINZIGE Größen-Stellschraube
  // ist jetzt textScale/zoom unten ("Widget-Größe" im Dashboard) — die skaliert das
  // ganze Widget proportional (Panel, Schrift, Buttons). Vorher war panelWidth ein
  // ZWEITER, unabhängiger Regler, der INNERHALB des gezoomten #accessify-mount lag
  // und so mit dem Zoom multiplizierte (520 px × 1,15 = 598 px) → für den Admin
  // unvorhersehbar. config.panelWidth wird bewusst ignoriert (Alt-Konfigs erben den
  // sauberen Standard). zIndex etc. bleiben unberührt.
  const panelW = '320px';
  // Textgrößen-Skalierung: Admin-konfiguriert im Dashboard (80–150 %).
  // Wir nutzen `zoom` am inneren Mount-Container — das skaliert ALLES
  // proportional (Text, Buttons, Paddings, Icons, Gaps), Layout bleibt
  // konsistent. `font-size: calc(...)` allein hat nur Text skaliert und
  // Buttons disproportional groß wirken lassen.
  const textScale = typeof c.textScalePercent === 'number'
    ? Math.max(0.6, Math.min(2, c.textScalePercent / 100))
    : 1;
  // Choose the wordmark variant based on the actual panel surface. The
  // default surface (#ffffff) keeps the gradient; the deep-indigo brand
  // surfaces flip to the white variant. createWidgetStyles is re-run on
  // every Dashboard live-preview message, so this stays correct as the
  // site owner edits brand colours.
  const logoVariant = pickLogoVariant(c.brandPrimaryColor as string | undefined);

  return `
    :host {
      all: initial;
      display: block !important;
      position: fixed !important;
      ${isBottom ? 'bottom: 20px' : 'top: 20px'} !important;
      ${isRight ? 'right: 20px' : 'left: 20px'} !important;
      z-index: ${z} !important;
      width: auto !important; height: auto !important;
      overflow: visible !important;
      contain: none !important;
      /* Brand body font: Inter (house font per brand canon — Switzer is
       * retired). NOT bundled: injectBrandFonts() is a no-op since R15-3
       * (Fontshare CORS/CSP killed the external stylesheet inject), so this
       * resolves to Inter only when the host page already serves it, else
       * the system stack. The wordmark in PanelHeader is now vector glyphs,
       * so the brand identity holds regardless of whether Inter loads. */
      font-family: "Inter", "Inter Placeholder", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      font-size: 16px;
      line-height: 1.5;
      /* color-scheme: light made the native scrollbar render light-on-light
       * against a dark brand surface — practically invisible, so the panel
       * looked unscrollable. auto lets the browser pick a scrollbar that
       * matches the host OS / brand surface contrast. */
      color-scheme: auto;
    }

    /* EyeAble-style self-dismiss: the host wrapper (index.ts) toggles the
     * data-accessify-hidden attribute on the host element. It MUST live here as
     * a :host() rule rather than an outer inline style, because per the CSS
     * Scoping spec an !important :host declaration from inside the shadow tree
     * wins over an outer inline !important — so inline display:none could never
     * beat the :host{display:block!important} above. Higher specificity
     * (:host([attr]) = 0,2,0 vs :host = 0,1,0) makes this the winner when set. */
    :host([data-accessify-hidden]) { display: none !important; }

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    /* Default theme = light, matches Dashboard-v2 surface. Site owners can
     * override every token via brandPrimaryColor / brandSurfaceColor / etc.
     * The previous brand-blue palette (#3d4775) is now opt-in, not default. */
    #accessify-mount {
      /* Admin-konfigurierbarer Textgrößen-Multiplikator (80–150 %). zoom
       * skaliert ALLES proportional — font-size, padding, gap, icon-size,
       * border-width. Wenn jemand 130 % wählt, wird das Widget visuell
       * 30 % größer, ohne dass das Layout bricht. */
      zoom: ${textScale};
      --gap: 14px;
      --pad: 18px;
      --radius: 12px;
      --radius-lg: 16px;
      --pill: 999px;
      --surface: #ffffff;
      --surface-card: #ffffff;
      --surface-dim: #f5f5f7;
      --surface-hover: #f0f2f6;
      --text: #101011;
      --text-dim: #606266;
      --border: #e8e8ec;
      --border-hl: #d4d4da;
      --accent: #c2473d;
      --accent-soft: rgba(194,71,61,0.12);
      /* --accent-on: WEISS. Brand-Regel (Accessify-Brand/DESIGN.md §2): jede
       * SOLIDE Markenfläche (Slate/Coral/Teal) trägt IMMER weißen Text — nie
       * Schwarz. Coral #c2473d gegen Weiß = 4.9:1 (AA). Das alte Default-Coral
       * #e8837c ist retired (zu hell → Weiß fiel auf 2.6:1; gilt jetzt nur noch
       * als Deko-FLÄCHE ohne Text). Kunden mit eigenem --accent bekommen via
       * brandAccentColor automatisch eine passende Textfarbe (pickReadableOnAccent). */
      --accent-on: #ffffff;
      /* Teal = „aktiviert" (Brand §1: aktivierte Features + Checkmarks = Teal).
       * Coral bleibt Akzent/CTA/Fokus-Ring; Teal signalisiert „an/erledigt".
       * Weiß auf Teal #118378 = 4.6:1 (AA). */
      --success: #118378;
      --success-soft: rgba(17,131,120,0.12);
      --success-on: #ffffff;
      --logo-color: #c2473d;
      --shadow: 0 18px 50px rgba(15,20,40,0.10), 0 2px 8px rgba(15,20,40,0.04);
      --fast: 140ms ease;
      ${brandOverrides}
    }

    /* R10-3: when config.theme === auto (default) and the host OS is set
     * to dark mode, switch to a dark token set. We wrap the whole
     * selector in :where() so its specificity stays at (0,0,0) — that
     * way any Dashboard-supplied brandSurfaceColor / brandTextColor /
     * etc. (above, specificity 0,1,0) still wins. Without this, the
     * dark block was beating Dashboard brand overrides and customers
     * saw a slate-grey panel instead of their configured colours. */
    @media (prefers-color-scheme: dark) {
      :where(#accessify-mount):where(:not([data-theme="light"]):not([data-theme="brand"])) {
        --surface: #1a1d29;
        --surface-card: #232735;
        --surface-dim: #2a2e3d;
        --surface-hover: #323647;
        --text: #f0f0f5;
        --text-dim: #a8aab8;
        --border: #3a3f51;
        --border-hl: #4d5267;
        --accent-soft: rgba(194,71,61,0.26);
        --success-soft: rgba(17,131,120,0.26);
        --shadow: 0 18px 50px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2);
      }
    }

    /* Explicit dark theme (user toggles it in the settings menu, or auto-mode
     * resolves to dark). This MUST use the id selector so it beats the base
     * #accessify-mount light tokens — the prefers-color-scheme block above is
     * wrapped in :where() (0,0,0 specificity) and on its own never overrides
     * the (1,0,0) base, which is why dark mode silently never applied. */
    #accessify-mount[data-theme="dark"] {
      --surface: #1a1d29;
      --surface-card: #232735;
      --surface-dim: #2a2e3d;
      --surface-hover: #323647;
      --text: #f0f0f5;
      --text-dim: #a8aab8;
      --border: #3a3f51;
      --border-hl: #4d5267;
      --accent-soft: rgba(194,71,61,0.26);
      --success-soft: rgba(17,131,120,0.26);
      --shadow: 0 18px 50px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2);
    }

    .sr-only { position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0; }
    /* R10-1: WCAG 2.4.7 - Focus Visible. The wildcard fallback below was
     * being shadowed by host UA styles in some browsers (audit recorded
     * "outline: none 0px" on Tab-focus despite this rule), so we duplicate
     * the focus styling on every interactive surface with !important and
     * pair the outline with an accent-soft box-shadow halo for a much
     * more readable ring at 1x zoom. */
    *:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; border-radius: 8px; }
    .accessify-panel button:focus-visible,
    .accessify-panel [role="switch"]:focus-visible,
    .accessify-panel [role="radio"]:focus-visible,
    .accessify-panel [role="button"]:focus-visible,
    .accessify-panel a:focus-visible,
    .accessify-panel input:focus-visible,
    .accessify-panel select:focus-visible {
      outline: 2px solid var(--accent) !important;
      outline-offset: 2px !important;
      box-shadow: 0 0 0 4px var(--accent-soft) !important;
      border-radius: 8px;
    }
    .accessify-trigger:focus-visible {
      outline: 3px solid var(--accent) !important;
      outline-offset: 3px !important;
      box-shadow: 0 0 0 6px var(--accent-soft) !important;
    }

    /* ─── Trigger ─── */
    /* Default look matches docs/brand/widget-icon.svg: full brand gradient
     * background (blue → mauve → coral) with the white person-in-ring glyph
     * filling the button. Site owners can still override via
     * brandTriggerColor / brandTriggerIconColor in the dashboard. */
    .accessify-trigger {
      position: static;
      z-index: ${z};
      width: 56px; height: 56px;
      padding: 0;
      border: none; border-radius: 16px;
      /* Gradient endet im aktuellen Brand-Coral #c2473d (4.9:1 gegen Weiß):
       * das weiße Glyph erreicht damit überall die 3:1 aus WCAG 1.4.11.
       * #e8837c (2.6:1) ist retired. */
      background: ${triggerBg || 'linear-gradient(135deg, #3d4775 0%, #4a5387 50%, #c2473d 100%)'};
      color: ${triggerIcon || '#ffffff'};
      cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 6px 20px rgba(50,60,100,0.5);
      transition: transform var(--fast), box-shadow var(--fast), opacity var(--fast);
      /* R11-1: "touch-action: none" blocked every browser-synthesized
       * click after touchend, so a real finger tap never opened the panel.
       * "manipulation" keeps pan/zoom semantics off the trigger (so the
       * pointer-down drag handler still works) but lets tap-to-click fire. */
      touch-action: manipulation;
      overflow: hidden;
    }
    @media (hover: hover) {
      .accessify-trigger:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(50,60,100,0.6); }
    }
    .accessify-trigger:active { transform: scale(0.96); }
    .accessify-trigger--hidden { display:none; }
    /* Trigger glyph fills the whole button (matches the brand icon asset). */
    .accessify-trigger svg { width: 100%; height: 100%; display: block; }
    .accessify-trigger--dragging { cursor: grabbing !important; opacity: 0.85; }

    /* Active count badge on trigger */
    .accessify-trigger-badge {
      position: absolute; top: -4px; right: -4px;
      min-width: 20px; height: 20px; padding: 0 5px;
      border-radius: 999px; background: var(--accent); color: #fff;
      font-size: 11px; font-weight: 700; line-height: 20px; text-align: center;
      pointer-events: none;
    }

    /* ─── Panel ─── */
    /* Desktop: kompaktes Popup, das unten rechts an der Trigger-Position
     * andockt (der Trigger wird beim Öffnen ausgeblendet, kein Overlap). Es
     * wächst nur so hoch wie sein Inhalt, gedeckelt auf 680px bzw. die
     * Viewport-Höhe minus Rand. Vorher war es eine VOLLHÖHEN-Schublade
     * (top:0;bottom:0;height:100vh) am rechten Rand — die überlappte auf der
     * Seite die Navigation und ihre linke Kante saß je nach Fensterbreite
     * mitten im Screen. Mobil (unten) bleibt die Vollbild-Schublade, dort ist
     * sie richtig. */
    .accessify-panel {
      position: fixed;
      ${isBottom ? 'bottom: 20px' : 'top: 20px'};
      ${isRight ? 'right: 20px' : 'left: 20px'};
      z-index: ${z - 1};
      width: min(${panelW}, calc(100vw - 40px));
      max-height: min(680px, calc(100vh - 40px));
      display: flex; flex-direction: column;
      border-radius: 20px;
      background: var(--surface);
      overflow: hidden;
      color: var(--text);
      box-shadow: var(--shadow);
      transition: opacity 200ms ease, transform 200ms ease, border-radius 240ms ease;
    }
    .accessify-panel[aria-hidden="true"] { opacity:0; transform:${hiddenTransform}; pointer-events:none; display:none; }
    .accessify-panel[aria-hidden="false"] { opacity:1; transform:translateX(0); }

    /* ─── Settings menu ("Einstellungsrad") — overlays the panel ─── */
    .accessify-settings {
      /* z-index MUSS über allem im Body liegen: die Feature-Karten-Info-Buttons
       * (.accessify-card-info, z-index 10) und deren Popover (z-index 30) sind
       * sonst SICHTBAR durch das Overlay durchgestochen — das ließ das alte
       * Settings-Overlay „kaputt/leer" aussehen. 50 deckt beide sicher ab. */
      position: absolute; inset: 0; z-index: 50;
      background: var(--surface);
      display: flex; flex-direction: column;
      border-radius: inherit;
    }
    .accessify-settings-head {
      display: flex; align-items: center; justify-content: space-between;
      padding: 14px var(--pad);
      border-bottom: 1px solid var(--border);
      background: var(--surface-dim);
      flex-shrink: 0;
    }
    .accessify-settings-title { font-size: 15px; font-weight: 600; color: var(--text); }
    .accessify-settings-body {
      flex: 1 1 auto; min-height: 0; overflow-y: auto;
      padding: var(--pad);
      display: flex; flex-direction: column; gap: var(--gap);
    }
    /* Volle-Breite Aktionszeile im Settings-Panel (z. B. „Alle zurücksetzen") */
    .accessify-settings-action {
      display: flex; align-items: center; gap: 10px;
      width: 100%; padding: 14px;
      border-radius: var(--radius); border: 1px solid var(--border);
      background: var(--surface-card); color: var(--text);
      cursor: pointer; font-family: inherit; font-size: 14px; font-weight: 600;
      text-align: left;
      transition: background var(--fast), border-color var(--fast);
    }
    .accessify-settings-action-icon {
      display: inline-flex; align-items: center; justify-content: center;
      width: 34px; height: 34px; border-radius: 10px;
      background: var(--surface-dim); color: var(--accent); flex-shrink: 0;
    }
    .accessify-settings-action-icon svg { width: 18px; height: 18px; }
    @media (hover: hover) {
      .accessify-settings-action:hover { background: var(--surface-hover); border-color: var(--border-hl); }
    }

    /* ─── Header ─── */
    .accessify-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 14px var(--pad);
      border-bottom: 1px solid var(--border);
      background: var(--surface-dim);
      flex-shrink: 0;
    }
    .accessify-header-left { display: flex; align-items: center; gap: 10px; }
    .accessify-header-actions { display: flex; align-items: center; gap: 6px; }

    .accessify-logo { display: inline-flex; color: var(--logo-color); }
    .accessify-logo svg { height: 24px; width: auto; }

    .accessify-header-btn {
      display: inline-flex; align-items: center; justify-content: center;
      width: 34px; height: 34px;
      border: 1px solid var(--border); border-radius: 10px;
      background: var(--surface); color: var(--text-dim);
      cursor: pointer; transition: background var(--fast), color var(--fast);
    }
    @media (hover: hover) {
      .accessify-header-btn:hover { background: var(--surface-hover); color: var(--text); }
    }
    .accessify-header-btn svg { width: 16px; height: 16px; }
    .accessify-header-btn--icon { padding: 0; }

    /* Schließen — die wichtigste Aktion im Panel, also die auffälligste.
     * Vorher war es auf transparenten Hintergrund UND transparenten Rahmen
     * gesetzt — das X war als EINZIGER Header-Button rahmen- und flächenlos und
     * verschwand neben den gerahmten Nachbarn. Genau deshalb war "wie mache ich
     * das wieder zu?" nicht beantwortbar. Jetzt: eigene Fläche, kräftigerer
     * Rahmen, 40x40 Trefferfläche (WCAG 2.5.8 Target Size locker erfüllt),
     * dickeres Kreuz und ein deutlicher Hover/Focus in der Akzentfarbe. */
    .accessify-header-btn--close {
      width: 40px; height: 40px;
      background: var(--surface-card);
      border-color: var(--border-hl);
      color: var(--text);
    }
    .accessify-header-btn--close svg {
      width: 20px; height: 20px;
      stroke-width: 2.5;
    }
    @media (hover: hover) {
      .accessify-header-btn--close:hover {
        background: var(--accent);
        border-color: var(--accent);
        color: var(--accent-on, #ffffff);
      }
    }

    /* Drag handle */
    .accessify-drag-handle {
      cursor: grab; touch-action: none;
    }
    .accessify-drag-handle:active { cursor: grabbing; }
    .accessify-drag-handle svg { stroke-width: 3; }

    /* ─── Shared Accordion (Language + Profiles) ─── */
    .accessify-lang-dropdown,
    .accessify-accordion {
      border-radius: var(--radius);
      border: 1px solid var(--border);
      overflow: clip;
    }

    .accessify-lang-toggle,
    .accessify-accordion-toggle {
      display: flex; align-items: center; justify-content: space-between;
      width: 100%; padding: 14px 14px;
      border: none; background: var(--surface-card);
      color: var(--text); cursor: pointer; font-family: inherit;
      font-size: 15px; font-weight: 700;
      transition: background var(--fast);
    }
    @media (hover: hover) {
      .accessify-lang-toggle:hover,
      .accessify-accordion-toggle:hover { background: var(--surface-hover); }
    }

    .accessify-lang-toggle-left,
    .accessify-accordion-toggle-left {
      display: flex; align-items: center; gap: 10px;
    }
    .accessify-lang-toggle-left svg,
    .accessify-accordion-toggle-left svg { width: 20px; height: 20px; color: var(--accent); }

    .accessify-lang-chevron,
    .accessify-accordion-chevron {
      width: 18px; height: 18px; color: var(--text-dim);
      transition: transform 200ms ease; flex-shrink: 0;
    }
    .accessify-lang-chevron--open,
    .accessify-accordion-chevron--open { transform: rotate(180deg); }

    /* Shared list inside accordion */
    .accessify-lang-list,
    .accessify-accordion-list {
      display: flex; flex-direction: column;
      max-height: 280px; overflow-y: auto;
      background: var(--surface-dim);
      border-top: 1px solid var(--border);
    }
    .accessify-lang-list::-webkit-scrollbar,
    .accessify-accordion-list::-webkit-scrollbar { width: 4px; }
    .accessify-lang-list::-webkit-scrollbar-thumb,
    .accessify-accordion-list::-webkit-scrollbar-thumb { background: var(--border-hl); border-radius: 2px; }

    /* Language options */
    .accessify-lang-option {
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px var(--pad);
      border: none; background: transparent;
      color: var(--text); cursor: pointer;
      font-size: 14px; font-weight: 500; font-family: inherit;
      transition: background var(--fast);
      min-height: 44px;
    }
    @media (hover: hover) {
      .accessify-lang-option:hover { background: var(--surface-hover); }
    }
    .accessify-lang-option[data-active="true"] { color: var(--accent); font-weight: 700; }
    .accessify-lang-option svg { width: 18px; height: 18px; color: var(--accent); flex-shrink: 0; }

    /* Profile options (with icon + description) */
    .accessify-accordion-option {
      display: flex; align-items: center; justify-content: space-between;
      padding: 14px var(--pad);
      border: none; background: transparent;
      color: var(--text); cursor: pointer;
      font-family: inherit; text-align: left;
      transition: background var(--fast);
    }
    @media (hover: hover) {
      .accessify-accordion-option:hover { background: var(--surface-hover); }
    }
    .accessify-accordion-option[data-active="true"] { background: var(--accent-soft); }

    .accessify-accordion-option-left {
      display: flex; align-items: center; gap: 12px; min-width: 0;
    }
    .accessify-accordion-option-icon {
      display: inline-flex; align-items: center; justify-content: center;
      width: 40px; height: 40px; border-radius: 12px;
      background: var(--surface-card); color: var(--accent); flex-shrink: 0;
    }
    .accessify-accordion-option-icon svg { width: 24px; height: 24px; stroke-width: 2.5; }
    .accessify-accordion-option-text {
      display: flex; flex-direction: column; gap: 2px; min-width: 0;
    }
    .accessify-accordion-option-name {
      font-size: 14px; font-weight: 700; line-height: 1.3;
    }
    .accessify-accordion-option-desc {
      font-size: 12px; font-weight: 400; color: var(--text-dim); line-height: 1.3;
    }
    .accessify-accordion-option > svg {
      width: 20px; height: 20px; color: var(--accent); flex-shrink: 0; margin-left: 8px;
    }

    /* ─── Scrollable Body ─── */
    .accessify-body {
      flex: 1 1 auto;
      min-height: 0;
      display: flex; flex-direction: column; gap: var(--gap);
      padding: var(--gap) var(--pad);
      /* overflow-y: scroll (not auto) so the scrollbar is reserved at
       * all times, even when content fits. Eliminates a class of bugs
       * where macOS' auto-hiding scrollbar made the panel look
       * unscrollable on dark brand surfaces. */
      overflow-y: scroll;
      overscroll-behavior: contain;
      /* Touch + trackpad: explicitly allow vertical pan so iOS Safari
       * + Chrome trackpad-scrolling routes the gesture to this element
       * instead of letting the host page swallow it. */
      touch-action: pan-y;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: thin;
      scrollbar-color: var(--border-hl) transparent;
    }
    .accessify-body::-webkit-scrollbar { width: 8px; }
    .accessify-body::-webkit-scrollbar-track { background: transparent; }
    .accessify-body::-webkit-scrollbar-thumb {
      background: var(--border-hl);
      border-radius: 4px;
      border: 2px solid transparent;
      background-clip: content-box;
    }
    .accessify-body::-webkit-scrollbar-thumb:hover { background: var(--accent); background-clip: content-box; }

    /* ─── Contrast Row ─── */
    .accessify-control-row {
      display: flex; flex-direction: column; gap: 10px;
      padding: 14px 16px;
      border-radius: var(--radius); border: 1px solid var(--border);
      background: var(--surface-card);
    }
    .accessify-control-label {
      display: flex; align-items: center; gap: 8px;
    }
    .accessify-control-label-icon {
      display: inline-flex; align-items: center; justify-content: center;
      width: 34px; height: 34px; border-radius: 10px;
      background: var(--surface-dim); color: var(--accent); flex-shrink: 0;
    }
    .accessify-control-label-icon svg { width: 18px; height: 18px; }
    .accessify-control-label-text { font-size: 14px; font-weight: 600; }

    /* Contrast chips */
    .accessify-chip-row {
      display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
    }
    /* 2-Optionen-Reihe (Kontrast: Sanft/Stark): füllt die Breite mit zwei
     * gleich großen Chips — vorher saßen 2 Chips im 3-Spalten-Raster und
     * ließen eine leere dritte Spalte (wirkte „zu groß für zwei Buttons"). */
    .accessify-chip-row--two { grid-template-columns: repeat(2, 1fr); }
    .accessify-chip {
      min-height: 48px; padding: 6px 8px;
      border-radius: 10px; border: 1px solid var(--border);
      background: var(--surface-dim); color: var(--text);
      cursor: pointer; font-size: 11px; font-weight: 600; font-family: inherit;
      display: inline-flex; align-items: center; justify-content: center;
      text-align: center; line-height: 1.25;
      transition: background var(--fast), color var(--fast), border-color var(--fast);
    }
    @media (hover: hover) {
      .accessify-chip:hover { border-color: var(--border-hl); background: var(--surface-hover); }
    }
    .accessify-chip[data-active="true"] {
      background: var(--accent); color: var(--accent-on);
      border-color: var(--accent);
      border-width: 2px;
      font-weight: 700;
    }
    .accessify-chip--icon { gap: 6px; flex-direction: column; }
    .accessify-chip-icon { display: inline-flex; width: 16px; height: 16px; flex-shrink: 0; }
    .accessify-chip-icon svg { width: 16px; height: 16px; }
    .accessify-chip-check {
      display: inline-flex; align-items: center; justify-content: center;
      width: 20px; height: 20px; border-radius: 50%;
      background: rgba(255,255,255,0.25); flex-shrink: 0;
    }
    .accessify-chip-check svg { width: 14px; height: 14px; }

    /* Text Size stepper */
    .accessify-control-row:has(.accessify-stepper) {
      flex-direction: row; align-items: center; justify-content: space-between; gap: var(--gap);
    }
    .accessify-stepper {
      display: inline-flex; align-items: center;
      border-radius: 10px; border: 1px solid var(--border);
      background: var(--surface-dim); height: 36px; overflow: hidden;
    }
    .accessify-stepper-btn {
      display: inline-flex; align-items: center; justify-content: center;
      width: 36px; height: 100%; border: none; background: transparent;
      color: var(--text); cursor: pointer; font-size: 18px; font-weight: 700;
      transition: background var(--fast); font-family: inherit;
    }
    @media (hover: hover) {
      .accessify-stepper-btn:hover:not(:disabled) { background: var(--surface-hover); }
    }
    .accessify-stepper-btn:disabled { opacity: 0.3; cursor: default; }
    .accessify-stepper-value {
      min-width: 48px; text-align: center;
      font-size: 13px; font-weight: 600; color: var(--text); user-select: none;
    }

    /* Text Size: uses same .accessify-chip-row / .accessify-chip as contrast */

    /* ─── Feature Cards (2-column grid) ─── */
    .accessify-features {
      display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--gap);
    }
    .accessify-card-wrap {
      position: relative;
      overflow: visible;
    }
    .accessify-card {
      position: relative; width: 100%;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      gap: 8px; padding: 18px 10px; min-height: 105px;
      border-radius: var(--radius-lg); border: 1px solid var(--border);
      background: var(--surface-card); color: var(--text);
      cursor: pointer; text-align: center; font-family: inherit;
      transition: transform var(--fast), border-color var(--fast), background var(--fast), box-shadow var(--fast);
    }
    @media (hover: hover) {
      .accessify-card:hover { transform: translateY(-2px); border-color: var(--border-hl); box-shadow: 0 4px 16px rgba(0,0,0,0.15); }
    }
    /* Aktiv = Teal (Brand: aktivierte Features/Checkmarks). Das Icon-Plättchen
     * wird zur soliden Teal-Fläche mit weißem Glyph (kein „Farbe auf eigenem
     * Tint" mehr — vorher coral-Icon auf coral-Tint ≈ 2.2:1, brand-verboten). */
    .accessify-card[data-active="true"] { border-color: var(--success); background: var(--success-soft); }
    .accessify-card[data-active="true"] .accessify-card-icon { background: var(--success); color: var(--success-on); }
    .accessify-card[data-active="true"] .accessify-card-label { color: var(--text); }
    .accessify-card-icon {
      display: inline-flex; align-items: center; justify-content: center;
      width: 48px; height: 48px; border-radius: 14px;
      background: var(--surface-dim); color: var(--accent);
      transition: background var(--fast), color var(--fast);
    }
    .accessify-card-icon svg { width: 28px; height: 28px; stroke-width: 2.5; }
    .accessify-card-label {
      font-size: 12px; font-weight: 600; line-height: 1.25;
      /* Immer 2 Zeilen Höhe reservieren: ein- und zweizeilige Labels nehmen so
       * gleich viel Platz ein → ALLE Karten exakt gleich hoch (egal ob „Lesemaske"
       * oder „Animationen stoppen"). em-basiert = skaliert mit Zoom. 1-zeilige
       * Labels werden vertikal mittig in den 2-Zeilen-Block gesetzt. */
      min-height: 2.5em;
      display: flex; align-items: center; justify-content: center;
    }

    /* Info icon (now a real <button> for keyboard + touch a11y) */
    .accessify-card-info {
      position: absolute; top: 6px; ${isRight ? 'right: 6px' : 'left: 6px'}; z-index: 10;
      display: inline-flex; align-items: center; justify-content: center;
      width: 24px; height: 24px; border-radius: 999px;
      color: var(--text-dim); cursor: pointer; background: none; border: none;
      opacity: 0.45; transition: opacity var(--fast), color var(--fast), background var(--fast);
      font-family: inherit;
    }
    .accessify-card-info svg { width: 14px; height: 14px; }
    @media (hover: hover) {
      .accessify-card-info:hover { opacity: 1; color: var(--accent); background: var(--surface-dim); }
    }
    .accessify-card-info:focus-visible {
      outline: 2px solid var(--accent); outline-offset: 2px; opacity: 1;
    }
    .accessify-card-info[aria-expanded="true"] {
      opacity: 1; color: var(--accent); background: var(--surface-dim);
    }

    /* Click-revealed info popover. WAI-ARIA tooltip pattern: button toggles
     * an absolutely positioned panel below the card. ESC + outside click
     * close it (handled in FeatureGrid.svelte). One open at a time.
     *
     * The popover spans the full card-wrap width (left:4px right:4px) instead
     * of using max-width on a max-content sized box. Reason: .accessify-panel
     * uses overflow:hidden, so any horizontal overflow gets clipped at the
     * panel edge. Constraining to card-wrap width keeps the popover inside
     * the panel regardless of which grid column the card sits in.
     *
     * Positioned UNDER the card-wrap (top: 100%) — placing it over the card
     * itself would cover the icon + label, defeating the explanation. With
     * z-index 30 the popover floats above the next card row; the user can
     * still scroll the body if a bottom-row card opens its popover and the
     * panel is short. */
    .accessify-info-popover {
      position: absolute;
      top: calc(100% + 6px); left: 4px; right: 4px;
      z-index: 30;
      display: flex; align-items: flex-start; gap: 8px;
      padding: 10px 10px 10px 12px;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06);
      color: var(--text);
      font-size: 12px; font-weight: 500; line-height: 1.45;
      text-align: left;
      animation: accessify-info-pop 140ms ease-out;
    }
    @keyframes accessify-info-pop {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .accessify-info-popover-arrow {
      position: absolute;
      top: -5px; ${isRight ? 'right: 11px' : 'left: 11px'};
      width: 10px; height: 10px;
      background: var(--surface);
      border-left: 1px solid var(--border);
      border-top: 1px solid var(--border);
      transform: rotate(45deg);
    }
    /* Nach OBEN geklappt (kein Platz unter der Karte — z. B. letzte Reihe im
     * Bottom-Sheet, wo das Popover sonst hinter dem Panel-Rand verschwindet):
     * Popover sitzt ÜBER der Karte, der Pfeil zeigt nach unten. */
    .accessify-info-popover[data-dir="up"] {
      top: auto; bottom: calc(100% + 6px);
      animation-name: accessify-info-pop-up;
    }
    @keyframes accessify-info-pop-up {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .accessify-info-popover[data-dir="up"] .accessify-info-popover-arrow {
      top: auto; bottom: -5px;
      border-left: none; border-top: none;
      border-right: 1px solid var(--border);
      border-bottom: 1px solid var(--border);
    }
    .accessify-info-popover-text {
      flex: 1 1 auto; min-width: 0;
    }
    .accessify-info-popover-close {
      flex: 0 0 auto;
      display: inline-flex; align-items: center; justify-content: center;
      /* 24px = WCAG-2.5.8-Minimum (vorher 20px); auf Touch via pointer:coarse 32px. */
      width: 24px; height: 24px; border-radius: 6px;
      background: none; border: none; color: var(--text-dim);
      cursor: pointer; padding: 0; margin: -2px -2px 0 0;
      font-family: inherit;
      transition: background var(--fast), color var(--fast);
    }
    .accessify-info-popover-close svg { width: 12px; height: 12px; }
    @media (hover: hover) {
      .accessify-info-popover-close:hover { background: var(--surface-dim); color: var(--text); }
    }
    .accessify-info-popover-close:focus-visible {
      outline: 2px solid var(--accent); outline-offset: 1px;
    }

    /* ─── Section Divider ─── */
    .accessify-section-divider {
      height: 1px;
      background: var(--border);
      margin: 4px 0;
      flex-shrink: 0;
    }


    /* ─── Footer ─── */
    .accessify-footer {
      display: flex; flex-direction: column;
      padding: 10px var(--pad);
      border-top: 1px solid var(--border);
      color: var(--text-dim); font-size: 11px;
      background: var(--surface-dim); flex-shrink: 0;
      gap: 10px;
    }

    /* Ehrliche Disclaimer-Zeile (Burggraben sichtbar). Bewusst ruhig und
       lesbar, nicht als Kleingedrucktes weggedimmt. */
    .accessify-footer-note {
      font-size: 11px; line-height: 1.45; color: var(--text-dim);
    }
    /* Herkunftszeile: normale unterstrichene Textlink-Zeile, erbt die
       Theme-Farben und bleibt damit auch im Hoher-Kontrast-Modus lesbar. */
    .accessify-footer-brand {
      font-size: 11px; line-height: 1.45;
      padding: 10px 0 2px; text-align: center;
    }
    .accessify-footer-brand a {
      color: var(--text-dim); text-decoration: underline; text-underline-offset: 2px;
    }
    .accessify-footer-brand a:hover { color: var(--text); }
    .accessify-footer-brand a:focus-visible {
      outline: 2px solid var(--accent); outline-offset: 2px; border-radius: 3px;
    }

    /* ─── Feedback-Eingang im Footer ───
     * Klickt der User auf den Trigger, klappt das Form inline auf. Nutzt die
     * Theme-Variablen, damit Hell/Dunkel/Hoher-Kontrast automatisch greifen.
     * Component-scoped Svelte-Styles funktionieren hier nicht (Shadow-DOM
     * isoliert sie aus dem document head) — alle Widget-Styles laufen über
     * diese zentrale Konstante, die das Loader-Script ins Shadow-Root injiziert. */
    /* Footer-Aktionszeile: Feedback-Trigger + „Widget ausblenden" nebeneinander.
       Beide sind content-breite Pills; wrap, falls die Panel-Breite eng wird. */
    .accessify-footer-actions {
      display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
    }
    /* Eingeklappt = content-breiter Trigger (sitzt neben dem Ausblenden-Button).
       Aufgeklappt = das Form nimmt via :has() die ganze Zeile, der Ausblenden-
       Button wandert in die nächste Reihe. */
    .accessify-feedback { display: inline-flex; }
    .accessify-feedback:has(.accessify-feedback-form) { flex: 1 1 100%; }
    .accessify-feedback * { box-sizing: border-box; }
    .accessify-feedback-trigger {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 6px 12px;
      background: transparent;
      border: 1px solid var(--border);
      border-radius: 999px;
      color: var(--text);
      font-size: 12px; font-family: inherit; cursor: pointer;
      min-height: 28px;
      transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
    }
    @media (hover: hover) {
      .accessify-feedback-trigger:hover {
        background: var(--accent-soft); border-color: var(--accent); color: var(--accent);
      }
    }
    .accessify-feedback-icon { display: inline-flex; align-items: center; }
    .accessify-feedback-icon svg { width: 14px; height: 14px; stroke-width: 2; }
    .accessify-feedback-form {
      display: flex; flex-direction: column;
      gap: 8px;
      padding: 10px;
      width: 100%;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 8px;
    }
    .accessify-feedback-label {
      font-size: 12px; font-weight: 600; color: var(--text);
    }
    .accessify-feedback-textarea {
      width: 100%; box-sizing: border-box;
      padding: 8px 10px;
      background: var(--surface); color: var(--text);
      border: 1px solid var(--border); border-radius: 6px;
      font-family: inherit; font-size: 13px; line-height: 1.45;
      resize: vertical; min-height: 70px;
    }
    .accessify-feedback-textarea:focus {
      outline: 2px solid var(--accent); outline-offset: 1px;
    }
    .accessify-feedback-status {
      padding: 6px 8px; border-radius: 6px;
      font-size: 11px; line-height: 1.4;
    }
    .accessify-feedback-status--ok { background: #ecfdf3; color: #067647; }
    .accessify-feedback-status--warn { background: #fff8e1; color: #b54708; }
    .accessify-feedback-status--err { background: #fef3f2; color: #b42318; }
    .accessify-feedback-detail { opacity: 0.75; font-size: 11px; }
    .accessify-feedback-actions {
      display: flex; justify-content: flex-end; gap: 6px;
    }
    .accessify-feedback-btn {
      padding: 5px 12px; border-radius: 999px;
      font-size: 12px; font-family: inherit; font-weight: 500;
      cursor: pointer; border: 1px solid transparent;
      min-height: 28px;
    }
    .accessify-feedback-btn--secondary {
      background: transparent; border-color: var(--border); color: var(--text);
    }
    .accessify-feedback-btn--primary {
      background: var(--accent); color: var(--on-accent, #ffffff);
    }
    .accessify-feedback-btn:disabled { opacity: 0.5; cursor: not-allowed; }

    /* ─── Feedback-Screenshot (Auto-Capture + Upload) ─── */
    .accessify-shot-input { display: none; }
    .accessify-shot {
      display: flex; align-items: center; gap: 10px;
      padding: 8px; width: 100%;
      background: var(--surface); border: 1px solid var(--border); border-radius: 8px;
    }
    .accessify-shot-thumb {
      width: 72px; height: 48px; flex-shrink: 0;
      object-fit: cover; object-position: top;
      border-radius: 4px; border: 1px solid var(--border);
    }
    .accessify-shot-meta { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .accessify-shot-title { font-size: 12px; font-weight: 600; color: var(--text); }
    .accessify-shot-replace {
      align-self: flex-start; padding: 0; background: none; border: none;
      font-size: 11px; font-family: inherit; color: var(--accent); cursor: pointer;
      text-decoration: underline; text-underline-offset: 2px;
    }
    .accessify-shot-remove {
      flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
      width: 26px; height: 26px; border-radius: 6px;
      background: var(--surface); border: 1px solid var(--border);
      color: var(--text-dim); cursor: pointer;
    }
    @media (hover: hover) {
      .accessify-shot-remove:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-soft); }
    }
    .accessify-shot-remove svg { width: 13px; height: 13px; }
    .accessify-shot-drop {
      display: flex; align-items: center; gap: 10px;
      width: 100%; padding: 10px; min-height: 44px;
      background: var(--surface);
      border: 1.5px dashed var(--border); border-radius: 8px;
      color: var(--text-dim); font-size: 12px; font-family: inherit;
      text-align: left; cursor: pointer;
      transition: border-color 120ms ease, background 120ms ease, color 120ms ease;
    }
    @media (hover: hover) {
      .accessify-shot-drop:hover { border-color: var(--accent); color: var(--accent); }
    }
    .accessify-shot-drop--drag { border-color: var(--accent); background: var(--accent-soft); color: var(--accent); }
    .accessify-shot-drop--busy { cursor: default; }
    .accessify-shot-ico {
      flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
      width: 28px; height: 28px; border-radius: 6px;
      background: var(--surface-card); color: var(--accent);
    }
    .accessify-shot-ico svg { width: 16px; height: 16px; }
    .accessify-shot-drop-text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
    .accessify-shot-hint { font-size: 11px; color: var(--text-dim); }
    .accessify-shot-spinner {
      flex-shrink: 0; width: 16px; height: 16px; border-radius: 50%;
      border: 2px solid var(--border); border-top-color: var(--accent);
      animation: accessify-shot-spin 0.7s linear infinite;
    }
    @keyframes accessify-shot-spin { to { transform: rotate(360deg); } }
    .accessify-shot-privacy { font-size: 11px; line-height: 1.4; color: var(--text-dim); }
    .accessify-footer-links {
      display: flex; align-items: center; gap: 8px;
    }
    /* Footer links use --text (≈ #101011) on --surface-dim (≈ #f5f5f7)
     * for an ~17:1 contrast ratio. Pre-R9-3 they were --accent (Korall
     * orange) which sits at ~2:1 against the dim surface and failed
     * WCAG AA 4.5:1 for body text. The underline doubles as a visual
     * affordance now that link colour matches surrounding copy. */
    .accessify-footer a {
      color: var(--text);
      text-decoration: underline;
      text-underline-offset: 2px;
      font-weight: 600;
    }
    @media (hover: hover) {
      .accessify-footer a:hover { color: var(--accent); text-decoration-color: var(--accent); }
    }
    .accessify-footer a:focus-visible {
      outline: 2px solid var(--accent);
      outline-offset: 2px;
      border-radius: 2px;
    }

    .accessify-footer-close {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 6px 14px; min-height: 34px;
      border: 1px solid var(--border); border-radius: 10px;
      background: var(--surface); color: var(--text-dim);
      cursor: pointer; font-size: 12px; font-weight: 600; font-family: inherit;
      transition: background var(--fast), color var(--fast), border-color var(--fast);
      flex-shrink: 0;
    }
    @media (hover: hover) {
      .accessify-footer-close:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-soft); }
    }
    .accessify-footer-close svg { width: 14px; height: 14px; flex-shrink: 0; }

    /* ─── „Widget ausblenden" (EyeAble-style self-dismiss) ───
     * Bewusst dezent (Sekundaer-Text, randlos-pillig wie der Feedback-Trigger),
     * damit es nicht mit den Funktions-Toggles konkurriert. Links-buendig als
     * eigene Footer-Zeile. */
    .accessify-hide-btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 6px 12px; min-height: 28px;
      background: transparent;
      border: 1px solid var(--border); border-radius: 999px;
      color: var(--text-dim);
      font-size: 12px; font-weight: 500; font-family: inherit; cursor: pointer;
      transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
    }
    @media (hover: hover) {
      .accessify-hide-btn:hover {
        background: var(--accent-soft); border-color: var(--accent); color: var(--accent);
      }
    }
    .accessify-hide-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
    .accessify-hide-icon { display: inline-flex; align-items: center; }
    .accessify-hide-icon svg { width: 14px; height: 14px; flex-shrink: 0; }

    /* „Widget ausblenden?" — kurze Erklärung + Bestätigung, bevor wirklich
     * ausgeblendet wird. Erscheint als eigene Zeile unter den Footer-Actions. */
    .accessify-hide-confirm {
      display: flex; flex-direction: column; gap: 8px;
      padding: 12px;
      background: var(--surface); border: 1px solid var(--border); border-radius: 8px;
    }
    .accessify-hide-confirm-title { font-size: 13px; font-weight: 600; color: var(--text); }
    .accessify-hide-confirm-body { font-size: 12px; line-height: 1.45; color: var(--text-dim); }
    .accessify-hide-confirm-actions { display: flex; justify-content: flex-end; gap: 6px; margin-top: 2px; }
    .accessify-hide-confirm-btn {
      padding: 6px 14px; min-height: 30px; border-radius: 999px;
      font-size: 12px; font-weight: 500; font-family: inherit; cursor: pointer;
      border: 1px solid transparent;
    }
    .accessify-hide-confirm-btn--secondary { background: transparent; border-color: var(--border); color: var(--text); }
    .accessify-hide-confirm-btn--primary { background: var(--accent); color: var(--on-accent, #ffffff); border-color: var(--accent); }
    @media (hover: hover) {
      .accessify-hide-confirm-btn--secondary:hover { border-color: var(--accent); color: var(--accent); }
      .accessify-hide-confirm-btn--primary:hover { filter: brightness(1.05); }
    }
    .accessify-hide-confirm-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

    /* ─── Reduced Motion ─── */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
    }

    /* ─── Mobile ─── */
    @media (max-width: 640px) {
      /* Das eigentliche Mobil-Layout ist ein Bottom-Sheet weiter unten
       * (zweiter max-width:640px-Block: top:auto; bottom:0; max-height:70dvh;
       * border-radius:22px 22px 0 0 + Slide-up). Dieser Block setzt nur die
       * Breite; der Bottom-Sheet-Block überschreibt Position/Höhe/Radius. */
      .accessify-panel { width: 100vw; }
      .accessify-features { gap: 8px; }
      /* Bottom-Sheet hat keine eigene Fußzeile mehr (die liegt im Settings-
       * Overlay). Damit die letzte Karte am Handy NICHT hinter dem iOS-Home-
       * Indicator / der Safari-Bottom-Bar verschwindet ("abgeschnitten"), muss
       * der Scroll-Container selbst unten den Safe-Area-Inset als Polster haben. */
      .accessify-body { padding: 8px 12px; padding-bottom: max(20px, env(safe-area-inset-bottom, 0px)); }
      .accessify-trigger {
        ${isBottom ? 'bottom: 16px' : 'top: 16px'};
        ${isRight ? 'right: 16px' : 'left: 16px'};
        width: 50px; height: 50px; border-radius: 14px;
      }
      /* „Großer Mauszeiger" + „Tastaturnavigation" werden jetzt capability-
       * basiert in FeatureGrid ausgeblendet (Touch-Gerät = pointer:coarse +
       * hover:none), nicht mehr per Viewport-Breite — ein schmales Desktop-
       * Fenster behält Maus/Tastatur und damit diese Funktionen. */
      .accessify-drag-handle { display: none; }
      .accessify-header { padding-top: max(14px, env(safe-area-inset-top, 0px)); }
      .accessify-footer { padding-bottom: max(10px, env(safe-area-inset-bottom, 0px)); }
    }
    @media (pointer: coarse) {
      .accessify-header-btn { min-width: 44px; min-height: 44px; }
      .accessify-footer-close { min-height: 44px; }
      /* Footer-Pills + Bestätigungs-Buttons auf Touch ebenfalls auf 44px:
       * gerade beim Ausblenden-Dialog entfernt ein Fehltipp das Widget
       * dauerhaft (Restore nur über Website-Daten löschen). */
      .accessify-feedback-trigger,
      .accessify-feedback-btn,
      .accessify-hide-btn,
      .accessify-hide-confirm-btn { min-height: 44px; }
      .accessify-info-popover-close { width: 32px; height: 32px; }
      .accessify-card-info { opacity: 0.7; }
    }

    /* R10-14: hide the entire widget when the page is being printed —
     * otherwise the trigger appears on every printed page and the panel
     * (if open at print time) wastes the first sheet. */
    @media print {
      #accessify-root, #accessify-skip-nav, #accessify-trigger-skeleton { display: none !important; }
    }

    /* R10-5: on extreme-narrow viewports (320 px) the panel <h2> was
     * collapsing to clientWidth: 1. Allow it to shrink + truncate
     * instead of forcing layout to stretch around it. */
    .accessify-header h2 {
      min-width: 0;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Brand-wordmark variant switch — pickLogoVariant() above decided how to
     * render the single vector wordmark in PanelHeader.svelte on the current
     * surface. Default is the slate→mauve→coral gradient; on a dark enough
     * brandPrimaryColor the gradient's indigo start would disappear into the
     * panel, so we flip the SAME mark to a solid white silhouette with a
     * filter (brightness(0) → all black, invert(1) → all white). One copy of
     * the glyph paths, both looks. */
    ${logoVariant === 'white' ? '.accessify-logo svg { filter: brightness(0) invert(1); }' : ''}

    /* ─── Mobile bottom-sheet (≤640px) — additive, desktop untouched ─── */
    /* On phones the side-docked panel becomes a native-feeling bottom sheet:
       full width, anchored to the bottom, rounded top, slides up from below,
       with a dimmed scrim behind and a grab bar to swipe down. Every rule
       lives inside this max-width query, so desktop is byte-for-byte the same.
       The scrim + grab elements are display:none above 640px. */
    .accessify-scrim { display: none; }
    .accessify-grab { display: none; }
    @media (max-width: 640px) {
      .accessify-scrim {
        display: block; position: fixed; inset: 0; z-index: ${z - 2};
        background: rgba(10, 14, 28, 0.5);
        opacity: 0; pointer-events: none;
        transition: opacity 220ms ease;
      }
      .accessify-scrim[data-open="true"] { opacity: 1; pointer-events: auto; }

      .accessify-panel {
        top: auto; bottom: 0; left: 0; right: 0;
        width: 100%; height: auto; max-height: 70dvh;
        border-radius: 22px 22px 0 0;
        box-shadow: 0 -12px 40px rgba(10, 14, 28, 0.28);
      }
      .accessify-panel[aria-hidden="true"]  { transform: translateY(100%); }
      .accessify-panel[aria-hidden="false"] { transform: translateY(0); }

      .accessify-grab {
        display: flex; align-items: center; justify-content: center;
        padding: 14px 0 12px; flex-shrink: 0;
        touch-action: none; cursor: grab;
      }
      .accessify-grab::before {
        content: ''; width: 42px; height: 5px; border-radius: 999px;
        background: var(--border-hl);
      }

      .accessify-drag-handle { display: none; }
      .accessify-footer { padding-bottom: max(14px, env(safe-area-inset-bottom)); }
    }
  `;
}
